import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import PayPalSubscriptionButtons from '../../components/PayPalSubscriptionButtons/PayPalSubscriptionButtons';
import { useAuth } from '../../contexts/AuthContext';

interface LocationState {
  freeTrialEligible?: boolean;
}

/** Delay (ms) after payment section is visible before loading PayPal. Avoids "initial parse" blocking by some ad blockers. */
const PAYPAL_LOAD_DELAY_MS = 400;

const API_BASE = process.env.REACT_APP_API_URL || '';

const OrderSummary: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user, refreshUser } = useAuth();
  const state = location.state as LocationState | null;
  const freeTrialEligible = state?.freeTrialEligible ?? false;
  const [showPayPalButtons, setShowPayPalButtons] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const paymentSectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    document.title = 'Stock Table - Order Summary';
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/pricing');
      return;
    }
    if (user?.hasProSubscription) {
      navigate('/pricing');
    }
  }, [isAuthenticated, user?.hasProSubscription, navigate]);

  // Load PayPal when the payment section is visible (no click). Some ad blockers allow scripts
  // triggered by viewport visibility but block scripts on initial page parse.
  useEffect(() => {
    const el = paymentSectionRef.current;
    if (!el) return;
    let timeoutId: number | null = null;
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry?.isIntersecting) return;
        observer.disconnect();
        timeoutId = window.setTimeout(() => {
          setShowPayPalButtons(true);
        }, PAYPAL_LOAD_DELAY_MS);
      },
      { rootMargin: '50px', threshold: 0.1 }
    );
    observer.observe(el);
    return () => {
      observer.disconnect();
      if (timeoutId !== null) window.clearTimeout(timeoutId);
    };
  }, []);

  const priceLine = freeTrialEligible
    ? '14-Day Free Trial, then $4.99 per month'
    : '$4.99 per month';

  const introLine = 'By proceeding with any of the options below, you agree to the below terms:';

  const bulletTerms = freeTrialEligible
    ? [
        'Your 14-day free trial will begin immediately, then your paid monthly subscription will begin automatically when your free trial ends. You can cancel anytime from the "Manage Billing" page.',
        'Once the free trial period ends and you are charged for the first time, you can cancel and request a full refund within 48 hours.',
        'After that, you can still cancel and request a refund anytime, but the amount refunded will be prorated based on the days remaining in your monthly subscription.',
        'Your payment will be handled by PayPal. If you choose to pay using credit card, Stock Table will not store your credit card number and related information.',
      ]
    : [
        'Your paid monthly subscription will begin immediately. You can cancel anytime from the "Manage Billing" page.',
        'You can cancel and request a refund anytime, but the amount refunded will be prorated based on the days remaining in your monthly subscription.',
        'Your payment will be handled by PayPal. If you choose to pay using credit card, Stock Table will not store your credit card number and related information.',
      ];

  if (!isAuthenticated || user?.hasProSubscription) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background-light dark:bg-background flex flex-col">
      {isRedirecting && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
          aria-live="polite"
          aria-busy="true"
        >
          <div className="bg-surface-light dark:bg-background-card rounded-xl shadow-xl p-8 mx-4 flex flex-col items-center gap-4 max-w-sm">
            <div className="w-10 h-10 border-2 border-primary dark:border-highlight-gold border-t-transparent rounded-full animate-spin" />
            <p className="text-center font-medium text-text-light-primary dark:text-text-primary">
              Setting up your subscription…
            </p>
            <p className="text-sm text-text-light-secondary dark:text-text-secondary text-center">
              You’ll be redirected to Manage Billing in a moment.
            </p>
          </div>
        </div>
      )}
      <Header />

      <main className="flex-grow max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="bg-surface-light dark:bg-background-card rounded-lg shadow-lg p-6 sm:p-8">
          <h1 className="text-3xl font-bold text-text-light-primary dark:text-text-primary mb-4">
            Order Summary
          </h1>

          <div className="bg-amber-50 dark:bg-amber-950/50 rounded-lg p-4 sm:p-5 mb-8 border border-amber-200/60 dark:border-amber-700/40">
            <h2 className="text-lg font-semibold text-text-light-primary dark:text-text-primary mb-2">
              Pro Plan
            </h2>
            <p className="text-sm text-text-light-secondary dark:text-text-secondary mb-3">
              Monthly subscription that gives you access to more lists, more tickers per list, and allows you to see all our Pro indicators.
            </p>
            <p className="text-base font-semibold text-text-light-primary dark:text-text-primary">
              {priceLine}
            </p>
          </div>

          <section className="mb-8 pb-8 border-b border-border-light dark:border-border-color">
            <p className="text-text-light-secondary dark:text-text-secondary text-sm sm:text-base mb-4">
              {introLine}
            </p>
            <ul className="list-disc pl-6 space-y-3 text-text-light-secondary dark:text-text-secondary text-sm sm:text-base">
              {bulletTerms.map((term, index) => (
                <li key={index}>{term}</li>
              ))}
            </ul>
          </section>

          <section ref={paymentSectionRef}>
            {showPayPalButtons ? (
              <PayPalSubscriptionButtons
                onApprove={async (data) => {
                  const token = localStorage.getItem('accessToken');
                  if (!token || !data.subscriptionID) return;
                  setIsRedirecting(true);
                  try {
                    await fetch(`${API_BASE}/api/v1/subscription/link`, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                      },
                      body: JSON.stringify({ subscriptionID: data.subscriptionID }),
                    });
                    await refreshUser();
                    navigate('/manage-billing', { state: { subscriptionActivated: true }, replace: true });
                  } catch (e) {
                    console.error('Failed to link subscription:', e);
                    setIsRedirecting(false);
                  }
                }}
              />
            ) : (
              <p className="text-text-light-secondary dark:text-text-secondary text-sm py-4">
                Loading payment options…
              </p>
            )}
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default OrderSummary;
